/**
 * Network Context Menu Component
 * Right-click context menu for network nodes with relevant actions
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Eye,
  EyeOff,
  Link,
  Share2,
  BookOpen,
  Calendar,
  MapPin,
  Users,
  Package,
  Copy,
  ExternalLink
} from 'lucide-react';

const NetworkContextMenu = ({
  node = null,
  position = { x: 0, y: 0 },
  isVisible = false,
  onClose = () => {},
  onAction = () => {},
  options = {}
}) => {
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(position);

  // Adjust menu position to stay within viewport
  useEffect(() => {
    if (!isVisible || !menuRef.current) return;

    const menu = menuRef.current;
    const rect = menu.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    let adjustedX = position.x;
    let adjustedY = position.y;

    // Adjust horizontal position
    if (position.x + rect.width > viewport.width) {
      adjustedX = position.x - rect.width;
    }

    // Adjust vertical position
    if (position.y + rect.height > viewport.height) {
      adjustedY = position.y - rect.height;
    }

    setMenuPosition({ x: adjustedX, y: adjustedY });
  }, [isVisible, position]);

  // Close menu on outside click
  useEffect(() => {
    if (!isVisible) return;

    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, onClose]);

  // Handle action click
  const handleActionClick = (action, data = {}) => {
    onAction(action, { node, ...data });
    onClose();
  };

  if (!isVisible || !node) {
    return null;
  }

  // Define menu items based on node type
  const getMenuItems = () => {
    const baseItems = [
      {
        id: 'details',
        label: 'Show Details',
        icon: Eye,
        action: 'show-details'
      },
      {
        id: 'highlight',
        label: 'Highlight Connections',
        icon: Link,
        action: 'highlight-connections'
      },
      {
        id: 'copy',
        label: 'Copy Name',
        icon: Copy,
        action: 'copy-name'
      }
    ];

    if (node.type === 'person') {
      return [
        ...baseItems,
        { type: 'separator' },
        {
          id: 'show-timeline',
          label: 'Show in Timeline',
          icon: Calendar,
          action: 'show-timeline'
        },
        {
          id: 'show-region',
          label: 'Show Regional Context',
          icon: MapPin,
          action: 'show-region'
        },
        {
          id: 'related-people',
          label: 'Find Related People',
          icon: Users,
          action: 'find-related-people'
        },
        {
          id: 'related-items',
          label: 'Find Related Items',
          icon: Package,
          action: 'find-related-items'
        }
      ];
    } else if (node.type === 'item') {
      return [
        ...baseItems,
        { type: 'separator' },
        {
          id: 'ownership-history',
          label: 'Ownership History',
          icon: Calendar,
          action: 'ownership-history'
        },
        {
          id: 'related-people',
          label: 'Related People',
          icon: Users,
          action: 'find-related-people'
        },
        {
          id: 'item-details',
          label: 'Item Information',
          icon: BookOpen,
          action: 'item-details'
        }
      ];
    }

    return baseItems;
  };

  const menuItems = getMenuItems();

  return (
    <div
      ref={menuRef}
      className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50 min-w-48"
      style={{
        left: menuPosition.x,
        top: menuPosition.y,
      }}
      data-testid="context-menu"
    >
      {/* Menu header with node info */}
      <div className="px-3 py-2 border-b border-gray-100">
        <div className="font-medium text-gray-800 truncate">
          {node.name || node.id}
        </div>
        {node.nameJp && node.nameJp !== node.name && (
          <div className="text-xs text-gray-500 truncate">{node.nameJp}</div>
        )}
        {node.type === 'person' && node.years && (
          <div className="text-xs text-gray-500">{node.years}</div>
        )}
      </div>

      {/* Menu items */}
      <div className="py-1">
        {menuItems.map((item, index) => {
          if (item.type === 'separator') {
            return (
              <div
                key={`separator-${index}`}
                className="my-1 border-t border-gray-100"
              />
            );
          }

          const IconComponent = item.icon;

          return (
            <button
              key={item.id}
              onClick={() => handleActionClick(item.action)}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              role="menuitem"
            >
              <IconComponent size={16} className="text-gray-500" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>

      {/* Advanced options for developers/researchers */}
      {options.showAdvanced && (
        <>
          <div className="my-1 border-t border-gray-100" />
          <div className="py-1">
            <button
              onClick={() => handleActionClick('inspect-data')}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <ExternalLink size={16} className="text-gray-500" />
              <span>Inspect Data</span>
            </button>
            <button
              onClick={() => handleActionClick('export-subgraph')}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <Share2 size={16} className="text-gray-500" />
              <span>Export Subgraph</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default NetworkContextMenu;